import styled from "styled-components";

const colors = {
  textPrimary: "#172B4D",
  textSecondary: "#6B778C",
  border: "#DFE1E6",
  link: "#2F6FED",
  success: "#2e7d32",
  successBg: "#2e7d32",
  danger: "#c62828",
  dangerSoft: "#fdecea",
  warning: "#1565C0",
  lockBg: "#f3f0ea",
  lockBgRejected: "#f5efe6",
};

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: ${colors.textPrimary};
  max-height: min(78vh, 760px);
  overflow-y: auto;
  padding: 20px 32px 24px;
  box-sizing: border-box;
  position: relative;
`;

export const CloseButtonIcon = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${colors.textSecondary};
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  z-index: 2;

  &:hover {
    background: #f1f3f5;
    color: ${colors.textPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding-right: 28px;
`;

export const ManifoldTitle = styled.h2`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.15;
`;

export const StatusLabel = styled.span<{
  $tone: "warning" | "success" | "danger";
}>`
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  padding-top: 4px;
  color: ${({ $tone }) =>
    $tone === "success"
      ? colors.success
      : $tone === "danger"
        ? colors.danger
        : colors.warning};
`;

export const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 6px;
  font-size: 15px;
  line-height: 1.45;
`;

export const DetailLabel = styled.span`
  color: ${colors.textPrimary};
  min-width: fit-content;
`;

export const DetailValue = styled.span`
  font-weight: 700;
`;

export const InlineGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 20px;
  align-items: baseline;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const InlinePairGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 20px;
  align-items: baseline;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const MotivoBox = styled.div`
  background: ${colors.dangerSoft};
  border-radius: 10px;
  padding: 14px 16px;
`;

export const MotivoTitle = styled.div`
  color: ${colors.danger};
  font-weight: 800;
  margin-bottom: 4px;
`;

export const MotivoText = styled.div`
  color: ${colors.textPrimary};
  font-size: 14px;
  line-height: 1.4;
`;

export const EnvasesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
`;

export const EnvasesHeading = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.textSecondary};
`;

export const EnvasesScroll = styled.div`
  min-height: 48px;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 6px;
  flex-shrink: 0;
`;

export const EnvasesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(16, auto);
  grid-auto-flow: column;
  column-gap: 16px;
  row-gap: 6px;
  align-content: start;
`;

export const EnvaseLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  width: 100%;
  min-width: 0;
  text-align: left;
  color: ${colors.link};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

export const EnvasesHint = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  text-align: center;
  margin-top: 2px;
`;

export const EnvasesTotal = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
`;

export const LockBanner = styled.div<{ $variant?: "approved" | "rejected" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ $variant }) =>
    $variant === "rejected" ? colors.lockBgRejected : colors.lockBg};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.textPrimary};
`;

export const ActionsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 8px;
`;

export const ApproveButton = styled.button`
  height: 44px;
  border: none;
  border-radius: 10px;
  background: ${colors.successBg};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    filter: brightness(0.95);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

export const RejectButton = styled.button`
  height: 44px;
  border: 1.5px solid ${colors.danger};
  border-radius: 10px;
  background: #fff;
  color: ${colors.danger};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: ${colors.dangerSoft};
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

export const CloseButton = styled.button`
  width: 100%;
  height: 44px;
  border: 1.5px solid #333;
  border-radius: 10px;
  background: #fff;
  color: ${colors.textPrimary};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: #f7f7f7;
  }
`;

export const RejectForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
`;

export const RejectTextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${colors.danger};
    box-shadow: 0 0 0 3px rgba(198, 40, 40, 0.12);
  }
`;

export const LoadingBox = styled.div`
  padding: 28px 8px;
  text-align: center;
  color: ${colors.textSecondary};
`;
